import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { AiResponse } from "../shared/types/domain";

interface CoPilotCardProps {
  aiResponse: AiResponse | null;
  loading: boolean;
  ageWeeks?: number;
  generatedAt?: string | null;
}

// Domain check fields that render only when the AI returns something other than "none"
const DOMAIN_FIELDS: Array<{ key: keyof AiResponse; labelKey: string }> = [
  { key: "clusterFeeding",       labelKey: "copilot.clusterFeeding" },
  { key: "feedSleepWindows",     labelKey: "copilot.feedSleepWindows" },
  { key: "hydrationSlope",       labelKey: "copilot.hydrationSlope" },
  { key: "growthForecast",       labelKey: "copilot.growthForecast" },
  { key: "sleepTrend",           labelKey: "copilot.sleepTrend" },
  { key: "pumpingTrend",         labelKey: "copilot.pumpingTrend" },
  { key: "feedSleepCorrelation", labelKey: "copilot.feedSleepCorrelation" },
  { key: "caregiverLoad",        labelKey: "copilot.caregiverLoad" },
  { key: "sleepRegressionRisk",  labelKey: "copilot.sleepRegressionRisk" },
  { key: "nightRisk",            labelKey: "copilot.nightRisk" },
  { key: "anomaly",              labelKey: "copilot.anomaly" },
  { key: "stoolPattern",         labelKey: "copilot.stoolPattern" },
];

// Always-rendered summary fields
const SUMMARY_FIELDS: Array<{ key: keyof AiResponse; labelKey: string }> = [
  { key: "weeklyDigest",        labelKey: "copilot.weeklyDigest" },
  { key: "developmentalLeap",   labelKey: "copilot.developmentalLeap" },
  { key: "personalizedBaseline", labelKey: "copilot.personalizedBaseline" },
];

const DEFAULT_LABELS: Record<string, string> = {
  "copilot.clusterFeeding":       "Cluster Feeding",
  "copilot.feedSleepWindows":     "Feed & Sleep Pattern",
  "copilot.hydrationSlope":       "Hydration",
  "copilot.growthForecast":       "Growth",
  "copilot.sleepTrend":           "Sleep Trend",
  "copilot.pumpingTrend":         "Pumping Trend",
  "copilot.feedSleepCorrelation": "Feed–Sleep Link",
  "copilot.caregiverLoad":        "Caregiver Load",
  "copilot.sleepRegressionRisk":  "Sleep Regression",
  "copilot.nightRisk":            "Tonight's Risk",
  "copilot.anomaly":              "Anomaly",
  "copilot.stoolPattern":         "Stool Pattern",
  "copilot.weeklyDigest":         "Weekly Digest",
  "copilot.developmentalLeap":    "Development",
  "copilot.personalizedBaseline": "Baby's Baseline",
};

export function CoPilotCard({ aiResponse, loading, ageWeeks, generatedAt }: CoPilotCardProps) {
  const { t } = useTranslation();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggle = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });

  if (loading) {
    // SUMMARY_FIELDS cards are always rendered once loaded, so reserving that many
    // placeholder slots (instead of just one) keeps the loading→loaded swap from
    // shifting everything below the Co-Pilot section.
    return (
      <>
        {SUMMARY_FIELDS.map(({ key }) => (
          <div key={key as string} className="copilot-card copilot-card--loading">
            <span className="copilot-card-label">✦ Co-Pilot</span>
            <div className="copilot-pulse" />
          </div>
        ))}
      </>
    );
  }

  if (!aiResponse) return null;

  const renderCard = (id: string, label: string, text: string, watch = false) => {
    const expanded = expandedIds.has(id);
    return (
      <button
        key={id}
        type="button"
        className={`copilot-card${watch ? " copilot-card--watch" : ""}${expanded ? " copilot-card--expanded" : ""}`}
        onClick={() => toggle(id)}
        aria-expanded={expanded}
      >
        <div className="copilot-card-summary">
          <span className="copilot-card-label">✦ {label}</span>
          <span className="copilot-card-chevron">{expanded ? "▴" : "▾"}</span>
        </div>
        {expanded && (
          <>
            <p>{text}</p>
            {watch && <p className="copilot-disclaimer">{t("copilotDisclaimer")}</p>}
          </>
        )}
      </button>
    );
  };

  // Domain cards — skip if AI returned "none"
  const domainCards = DOMAIN_FIELDS.flatMap(({ key, labelKey }) => {
    const value = aiResponse[key];
    if (typeof value !== "string" || value.trim().toLowerCase() === "none") return [];
    const label = t(labelKey, { defaultValue: DEFAULT_LABELS[labelKey] ?? labelKey });
    return [renderCard(key as string, label, value)];
  });

  // Always-shown summary cards
  const summaryCards = SUMMARY_FIELDS.map(({ key, labelKey }) => {
    const value = aiResponse[key];
    const text = typeof value === "string" ? value : "";
    const label = t(labelKey, { defaultValue: DEFAULT_LABELS[labelKey] ?? labelKey });
    return renderCard(key as string, label, text);
  });

  // Week recap at the bottom
  const recapId = "week-recap";
  const recapExpanded = expandedIds.has(recapId);
  const weekRecapCard = aiResponse.weekRecap ? (
    <button
      type="button"
      className={`copilot-card copilot-card--letter${recapExpanded ? " copilot-card--expanded" : ""}`}
      onClick={() => toggle(recapId)}
      aria-expanded={recapExpanded}
    >
      <div className="copilot-card-summary">
        <span className="copilot-card-label">
          ✦ {ageWeeks != null
            ? t("copilotWeekRecap", { week: ageWeeks, defaultValue: `Week ${ageWeeks} Recap` })
            : t("copilotWeekRecapGeneric", { defaultValue: "Weekly Recap" })}
        </span>
        <span className="copilot-card-chevron">{recapExpanded ? "▴" : "▾"}</span>
      </div>
      {recapExpanded && <p>{aiResponse.weekRecap}</p>}
    </button>
  ) : null;

  const hasContent = domainCards.length > 0 || summaryCards.length > 0 || weekRecapCard;
  if (!hasContent) return null;

  return (
    <>
      {domainCards}
      {summaryCards}
      {weekRecapCard}
      {generatedAt && (
        <p className="copilot-generated-at">
          ✦ {t("copilotUpdated", { defaultValue: "Updated" })} {new Date(generatedAt).toLocaleString()}
        </p>
      )}
    </>
  );
}
